import React, { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { team } from '../../data/mockData';
import { useLanguage } from '../../context/LanguageContext';
import { Users, Scale, Headphones } from 'lucide-react';

function getInitials(name) {
  return name.split(' ').map((n) => n[0]).filter((c) => c && c === c.toUpperCase()).slice(0, 2).join('');
}

export default function TeamSection() {
  const { t, tl } = useLanguage();
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.1 });
  const [activeTab, setActiveTab] = useState('practitioners');

  const tabs = [
    { key: 'practitioners', labelKey: 'team_tab_practitioners', icon: Scale, data: team.practitioners },
    { key: 'lawyers', labelKey: 'team_tab_lawyers', icon: Users, data: team.lawyers },
    { key: 'support', labelKey: 'team_tab_support', icon: Headphones, data: team.support },
  ];

  const activeData = tabs.find((tab) => tab.key === activeTab);

  return (
    <section id="team" className="py-16 sm:py-24 lg:py-32 bg-[#111114]">
      <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 animate-reveal ${isVisible ? 'visible' : ''}`}>
        <div className="flex items-center gap-4 mb-6">
          <span className="text-[#c9a96e] text-xs font-semibold tracking-[0.25em] uppercase">{t('team_label')}</span>
          <div className="h-px flex-1 bg-[#2a2a30]" />
        </div>

        <div className="max-w-2xl mb-8 sm:mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#f0ebe3] leading-tight">
            {t('team_h2_1')}
            <span className="text-gold-gradient block mt-1">{t('team_h2_2')}</span>
          </h2>
          <p className="text-[#8a857e] text-sm sm:text-base leading-relaxed mt-4 sm:mt-6">{t('team_sub')}</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 sm:mb-12">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-3 sm:px-5 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-medium tracking-wide ${
                  isActive ? 'bg-[#c9a96e]/10 text-[#c9a96e] border border-[#c9a96e]/30' : 'bg-[#1a1a1f] text-[#8a857e] border border-[#1e1e24] hover:text-[#c9a96e] hover:border-[#c9a96e]/20'
                }`}
                style={{ transition: 'color 0.3s ease, border-color 0.3s ease, background-color 0.3s ease' }}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {t(tab.labelKey)}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {activeData && activeData.data.map((member, i) => (
            <div
              key={member.name}
              className="group p-5 sm:p-6 border border-[#1e1e24] rounded-xl bg-[#0a0a0c]/50 service-card"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: `opacity 0.5s ease ${i * 0.08}s, transform 0.5s ease ${i * 0.08}s`,
              }}
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#c9a96e]/10 flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-[#c9a96e]/15" style={{ transition: 'background-color 0.4s ease' }}>
                <span className="text-[#c9a96e] font-heading text-base sm:text-lg font-bold">{getInitials(member.name)}</span>
              </div>
              <h4 className="text-[#f0ebe3] font-medium text-sm sm:text-[15px] mb-1">{member.name}</h4>
              <p className="text-[#c9a96e]/60 text-xs font-medium tracking-wider uppercase">{tl(member.role)}</p>
              {member.description && <p className="text-[#6a6560] text-xs mt-2 sm:mt-3 leading-relaxed">{tl(member.description)}</p>}
            </div>
          ))}
        </div>

        <p className="text-[#4a4540] text-xs mt-6 sm:mt-8 italic">{t('team_note')}</p>
      </div>
    </section>
  );
}
